import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import axiosInstance from '../../helpers/axios';
import ManualAssignModal from './ManualAssignModal';
import ExportFreeLoansModal from './ExportFreeLoansModal';
import ImportAssignmentModal from './ImportAssignmentModal';
import PaginationTable from '../status/PaginationTable';

const ROWS_PER_PAGE_OPTIONS = [15, 50, 100, 500];
const FUND_CHIP_COLORS = ['primary', 'secondary', 'success', 'warning', 'info'];

function formatToLakhs(amount) {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${parseFloat((amount / 10000000).toFixed(2))} Cr`;
    if (amount >= 100000) return `₹${parseFloat((amount / 100000).toFixed(2))} L`;
    return `₹${Number(amount).toLocaleString('en-IN')}`;
}

function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fundChipColor(fundId) {
    if (!fundId) return 'default';
    let hash = 0;
    for (let i = 0; i < fundId.length; i++) hash = (hash * 31 + fundId.charCodeAt(i)) % FUND_CHIP_COLORS.length;
    return FUND_CHIP_COLORS[hash];
}

function HypothecationTableSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

function StatCard({ label, value, icon: Icon, color, bg }) {
    return (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '12px', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon sx={{ fontSize: 28, color }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
                <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', lineHeight: 1.4, marginTop: '2px' }}>{label}</Typography>
            </Box>
        </Box>
    );
}

export default function LoanHypothecationTab() {
    const [funds, setFunds] = useState([]);

    const [loans, setLoans] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState({ totalActive: 0, totalReleased: 0, totalValue: 0 });
    const [freeCount, setFreeCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);

    const [draftFundId, setDraftFundId] = useState('');
    const [draftStatus, setDraftStatus] = useState('');
    const [draftFrom, setDraftFrom] = useState(null);
    const [draftTo, setDraftTo] = useState(null);
    const [draftSearch, setDraftSearch] = useState('');
    const [appliedFilters, setAppliedFilters] = useState({ fundId: '', status: '', dateFrom: '', dateTo: '', search: '' });

    const [releaseTarget, setReleaseTarget] = useState(null);
    const [releaseError, setReleaseError] = useState(null);

    const [manualAssignOpen, setManualAssignOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        axiosInstance
            .get('/admin-settings/funds')
            .then((response) => setFunds(response.data.data.funds))
            .catch(() => setFunds([]));
    }, []);

    const loadFreeCount = useCallback(() => {
        axiosInstance
            .get('/admin-settings/loan-hypothecation/available', { params: { limit: 1 } })
            .then((response) => setFreeCount(response.data.data.total))
            .catch(() => setFreeCount(0));
    }, []);

    const loadLoans = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);
        axiosInstance
            .get('/admin-settings/loan-hypothecation', {
                params: {
                    page: currentPage,
                    limit: rowsPerPage,
                    fundId: appliedFilters.fundId || undefined,
                    status: appliedFilters.status || undefined,
                    dateFrom: appliedFilters.dateFrom || undefined,
                    dateTo: appliedFilters.dateTo || undefined,
                    search: appliedFilters.search || undefined,
                },
            })
            .then((response) => {
                setLoans(response.data.data.loans);
                setTotal(response.data.data.total);
                setStats(response.data.data.stats);
            })
            .catch(() => setErrorMessage('Could not load hypothecated loans.'))
            .finally(() => setIsLoading(false));
    }, [currentPage, rowsPerPage, appliedFilters]);

    useEffect(() => {
        loadFreeCount();
    }, [loadFreeCount]);

    useEffect(() => {
        loadLoans();
    }, [loadLoans]);

    const handleApplyFilters = () => {
        setCurrentPage(1);
        setAppliedFilters({
            fundId: draftFundId,
            status: draftStatus,
            dateFrom: draftFrom ? draftFrom.format('YYYY-MM-DD') : '',
            dateTo: draftTo ? draftTo.format('YYYY-MM-DD') : '',
            search: draftSearch.trim(),
        });
    };

    const handleRowsPerPageChange = (size) => {
        setRowsPerPage(size);
        setCurrentPage(1);
    };

    const handleRelease = () => {
        if (!releaseTarget) return;
        setReleaseError(null);
        axiosInstance
            .post(`/admin-settings/loan-hypothecation/release/${releaseTarget.id}`)
            .then(() => {
                setReleaseTarget(null);
                setSnackbar({ open: true, message: 'Loan released successfully', severity: 'success' });
                loadLoans();
                loadFreeCount();
            })
            .catch((error) => {
                setReleaseError(error?.response?.data?.message || 'Could not release loan.');
            });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Box sx={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
                    <StatCard label="Total Hypothecated" value={stats.totalActive + stats.totalReleased} icon={AssignmentOutlinedIcon} color="#2563eb" bg="#EFF6FF" />
                    <StatCard label="Active" value={stats.totalActive} icon={CheckCircleOutlineIcon} color="#16a34a" bg="#F0FDF4" />
                    <StatCard label="Released" value={stats.totalReleased} icon={TaskAltOutlinedIcon} color="#64748b" bg="#F1F5F9" />
                    <StatCard label="Unhypothecated (Available)" value={freeCount} icon={HourglassEmptyOutlinedIcon} color="#d97706" bg="#FFFBEB" />
                    <StatCard label="Hypothecated Value" value={formatToLakhs(stats.totalValue)} icon={PaidOutlinedIcon} color="#D9480F" bg="#FEF2ED" />
                </Box>

                <Box sx={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <Button variant="contained" color="success" onClick={() => setExportOpen(true)}>📥 Export Free Loans</Button>
                    <Button variant="contained" color="warning" onClick={() => setImportOpen(true)}>📤 Import Assignment</Button>
                    <Button variant="contained" onClick={() => setManualAssignOpen(true)}>☑️ Manual Assign</Button>
                </Box>

                <Alert severity="info" sx={{ marginBottom: '20px' }}>
                    Bulk Workflow: Export free loans → Fill fund_id column in Excel → Import back → Review &amp; confirm
                </Alert>

                <Box sx={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Select
                        size="small"
                        displayEmpty
                        value={draftFundId}
                        onChange={(e) => setDraftFundId(e.target.value)}
                        sx={{ minWidth: 180 }}
                    >
                        <MenuItem value="">All Funds</MenuItem>
                        {funds.map((f) => (
                            <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                        ))}
                    </Select>

                    <Select
                        size="small"
                        displayEmpty
                        value={draftStatus}
                        onChange={(e) => setDraftStatus(e.target.value)}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="released">Released</MenuItem>
                    </Select>

                    <DatePicker
                        label="From Date"
                        value={draftFrom}
                        onChange={setDraftFrom}
                        slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                    />
                    <DatePicker
                        label="To Date"
                        value={draftTo}
                        onChange={setDraftTo}
                        slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                    />

                    <TextField
                        size="small"
                        placeholder="Search by Loan ID or borrower name..."
                        value={draftSearch}
                        onChange={(e) => setDraftSearch(e.target.value)}
                        sx={{ minWidth: 240 }}
                    />

                    <Button variant="contained" onClick={handleApplyFilters}>🔍 Filter</Button>
                </Box>

                {errorMessage && <Alert severity="error" sx={{ marginBottom: '12px' }}>{errorMessage}</Alert>}

                <Paper variant="outlined" sx={{ borderRadius: '8px' }}>
                    <TableContainer>
                        <Table sx={{ tableLayout: 'fixed' }}>
                            <TableHead sx={{ backgroundColor: '#F8F8F8' }}>
                                <TableRow>
                                    <TableCell sx={{ width: '11%' }}>Loan ID</TableCell>
                                    <TableCell sx={{ width: '13%' }}>Borrower</TableCell>
                                    <TableCell sx={{ width: '9%' }}>Amount</TableCell>
                                    <TableCell sx={{ width: '8%' }}>Tenure</TableCell>
                                    <TableCell sx={{ width: '9%' }}>EMI</TableCell>
                                    <TableCell sx={{ width: '11%' }}>Disbursement Date</TableCell>
                                    <TableCell sx={{ width: '11%' }}>Fund</TableCell>
                                    <TableCell sx={{ width: '9%' }}>Status</TableCell>
                                    <TableCell sx={{ width: '10%' }}>Hypothecated Date</TableCell>
                                    <TableCell sx={{ width: '9%' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            {isLoading ? (
                                <HypothecationTableSkeleton />
                            ) : loans.length === 0 ? (
                                <TableBody>
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', padding: '32px' }}>
                                            No hypothecated loans match the current filters.
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {loans.map((loan) => (
                                        <TableRow key={loan.id} hover>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{loan.loanId}</TableCell>
                                            <TableCell>{loan.borrowerName || '—'}</TableCell>
                                            <TableCell>{formatToLakhs(loan.amount)}</TableCell>
                                            <TableCell>{loan.tenure ? `${loan.tenure} mo` : '—'}</TableCell>
                                            <TableCell>{formatToLakhs(loan.emi)}</TableCell>
                                            <TableCell>{formatDate(loan.disbursementDate)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" color={fundChipColor(loan.fundId)} label={loan.fundName || '—'} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={loan.status === 'active' ? 'success' : 'default'}
                                                    label={loan.status === 'active' ? 'Active' : 'Released'}
                                                />
                                            </TableCell>
                                            <TableCell>{formatDate(loan.hypothecatedAt)}</TableCell>
                                            <TableCell align="right">
                                                {loan.status === 'active' ? (
                                                    <Button size="small" color="warning" onClick={() => { setReleaseTarget(loan); setReleaseError(null); }}>
                                                        Release
                                                    </Button>
                                                ) : (
                                                    <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                                        Released {formatDate(loan.releasedAt)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </Paper>

                {total > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '16px', marginBottom: '10px', flexWrap: 'wrap', gap: '4px' }}>
                        <Box sx={{ fontWeight: 'bold', fontFamily: 'Inter-Medium', fontSize: '14px', marginRight: '10px' }}>
                            Showing {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, total)} records of {total} |
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 600, marginRight: '8px' }}>Rows Per Pages :</Typography>
                            {ROWS_PER_PAGE_OPTIONS.map((size) => (
                                <Box
                                    key={size}
                                    component="span"
                                    onClick={() => handleRowsPerPageChange(size)}
                                    sx={{
                                        cursor: 'pointer',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        color: rowsPerPage === size ? '#D32028' : 'black',
                                        backgroundColor: rowsPerPage === size ? '#FFF2F3' : 'transparent',
                                    }}
                                >
                                    {size}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <PaginationTable
                    startPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={total}
                    paginate={setCurrentPage}
                />

                <Dialog open={Boolean(releaseTarget)} onClose={() => setReleaseTarget(null)}>
                    <DialogTitle>Release Loan</DialogTitle>
                    <DialogContent>
                        {releaseError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{releaseError}</Alert>}
                        Are you sure you want to release loan {releaseTarget?.loanId} from {releaseTarget?.fundName}?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setReleaseTarget(null)}>Cancel</Button>
                        <Button variant="contained" color="warning" onClick={handleRelease}>Release</Button>
                    </DialogActions>
                </Dialog>

                <ManualAssignModal
                    open={manualAssignOpen}
                    onClose={() => setManualAssignOpen(false)}
                    onSuccess={(message) => {
                        loadLoans();
                        loadFreeCount();
                        if (message) setSnackbar({ open: true, message, severity: 'success' });
                    }}
                />

                <ExportFreeLoansModal open={exportOpen} onClose={() => setExportOpen(false)} />

                <ImportAssignmentModal
                    open={importOpen}
                    onClose={() => setImportOpen(false)}
                    onSuccess={(message) => {
                        loadLoans();
                        loadFreeCount();
                        if (message) setSnackbar({ open: true, message, severity: 'success' });
                    }}
                />

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
}
